import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskCard from '../TaskCard';
import { resolveLabels } from '../../extensions/bundled/task-manager/boardLabels';
import { ColumnDropZone } from './ColumnDropZone';
import ColumnHeader from './ColumnHeader';
import './KanbanColumn.css';

// Big boards (owner, 13.10.2026: dragging between lists failed at 80+ tasks):
// every prop handed to the (memoised) TaskCard is stable, so a drag step
// re-renders only the cards whose task actually changed, not all of them.
const SortableTaskCard = React.memo(function SortableTaskCard({ task, onOpenTask, onChangeTask, onDeleteTask, justCreatedRef, memberById, assignCtx, palette }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task:${task.id}`,
  });
  const id = task.id;
  const labels = useMemo(() => resolveLabels(task, palette), [task, palette]);
  const dragHandleProps = useMemo(() => ({ ...attributes, ...listeners }), [attributes, listeners]);
  const onAssign = useMemo(() => (assignCtx ? (to) => onChangeTask(id, { assigneeId: to }) : null), [assignCtx, onChangeTask, id]);
  const onShare = useMemo(() => (assignCtx ? () => assignCtx.onShare?.(task) : null), [assignCtx, task]);
  const onChange = useCallback((patch) => onChangeTask(id, patch), [onChangeTask, id]);
  const onDelete = useCallback(() => onDeleteTask(id), [onDeleteTask, id]);
  const onOpenDetail = useCallback(() => onOpenTask(id), [onOpenTask, id]);
  return (
    // While dragged, the card's place shows as a dashed slot (card 2.5); the
    // lifted copy is TaskBoardTab's DragOverlay.
    <div ref={setNodeRef} className={isDragging ? 'task-board-card-sortable task-board-card-slot' : 'task-board-card-sortable'} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <TaskCard
        task={task}
        assignee={task.assigneeId ? memberById?.get(String(task.assigneeId)) || null : null}
        members={assignCtx?.members}
        currentUserId={assignCtx?.currentUserId}
        labels={labels}
        onAssign={onAssign}
        onShare={onShare}
        dragHandleProps={dragHandleProps}
        onChange={onChange}
        onDelete={onDelete}
        onOpenDetail={onOpenDetail}
        justCreatedRef={justCreatedRef}
      />
    </div>
  );
});

function Column({ group, index = 0, progress, showProgress = true, tasks, onOpenTask, onAddTask, onDeleteGroup, onRenameGroup, onChangeTask, onDeleteTask, justCreatedRef, justCreatedGroupsRef, memberById, assignCtx, palette }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `group:${group.id}`,
  });
  const [editing, setEditing] = useState(false);
  // A list created just now (via "Add list") starts in rename mode; it removes
  // itself from the set so re-mounting an existing list never steals focus.
  useEffect(() => {
    if (justCreatedGroupsRef?.current?.has(group.id)) {
      justCreatedGroupsRef.current.delete(group.id);
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);
  const onDelete = useCallback(() => onDeleteGroup(group), [onDeleteGroup, group]);
  const dragHandleProps = useMemo(() => ({ ...attributes, ...listeners }), [attributes, listeners]);
  const itemIds = useMemo(() => tasks.map((t) => `task:${t.id}`), [tasks]);
  const addTask = (
    <button type="button" className="task-board-add-task-btn task-board-add-task-btn--row" onClick={() => onAddTask(group.id)}>
      <Plus size={13} /> Add task
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="task-board-column"
    >
      <ColumnHeader
        group={group}
        index={index}
        progress={progress}
        showProgress={showProgress}
        editing={editing}
        setEditing={setEditing}
        onRename={onRenameGroup}
        onDelete={onDelete}
        dragHandleProps={dragHandleProps}
      />
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <ColumnDropZone groupId={group.id} scroll>
          {tasks.map((t) => (
            <SortableTaskCard key={t.id} task={t} onOpenTask={onOpenTask} onChangeTask={onChangeTask} onDeleteTask={onDeleteTask} justCreatedRef={justCreatedRef} memberById={memberById} assignCtx={assignCtx} palette={palette} />
          ))}
          {/* An EMPTY list puts "Add task" inside the drop zone (owner,
              13.10.2026): it then sits 10 px under the header like a first
              card, instead of under the empty zone's reserved height, and the
              row itself is where a dragged card lands. */}
          {!tasks.length && addTask}
        </ColumnDropZone>
      </SortableContext>
      {tasks.length > 0 && addTask}
    </div>
  );
}

// Memoised (big boards): TaskBoardTab hands each list a stable task array and
// progress (useGroupTasks), so a drag step re-renders only the lists it touched.
export default React.memo(Column);
